import { useEffect, useMemo, useState } from "react";
import type { PointerEvent as ReactPointerEvent } from "react";
import type { ClientMessage, RoomPublicState } from "../shared/types";
import { createPuzzlePieces } from "../shared/game";

interface GameProps { state: RoomPublicState; send: (message: ClientMessage) => boolean }

export function GuessNumber({ state, send }: GameProps) {
  const [guess, setGuess] = useState("");
  const hint = state.player?.guessHint;
  const history = state.player?.score?.guessHistory ?? [];
  return <section className="game-card game-card--guess">
    <div className="number-range"><span>1</span><i /><strong>?</strong><i /><span>100</span></div>
    <h2>Find your hidden number</h2><p>Enter a guess. We’ll tell you to go higher or lower.</p>
    <form onSubmit={(event) => { event.preventDefault(); const value = Number(guess); if (Number.isInteger(value) && value >= 1 && value <= 100) { send({ type: "guess", value }); setGuess(""); } }}>
      <input inputMode="numeric" pattern="[0-9]*" min="1" max="100" value={guess} onChange={(event) => setGuess(event.target.value)} placeholder="1–100" aria-label="Your guess" autoFocus />
      <button className="button button--primary" type="submit">GUESS</button>
    </form>
    {hint && <div className={`game-feedback game-feedback--${hint}`}>{hint === "correct" ? "🎉 Correct!" : hint === "higher" ? "⬆ Go higher" : "⬇ Go lower"}</div>}
    {history.length > 0 && <div className="guess-history"><strong>Your guesses</strong><div>{[...history].reverse().map((attempt, index) => <span className={`guess-chip guess-chip--${attempt.hint}`} key={`${attempt.value}-${history.length - index}`}>{attempt.value} {attempt.hint === "higher" ? "↑" : attempt.hint === "lower" ? "↓" : "✓"}</span>)}</div></div>}
    <p className="attempts">{state.player?.score?.attempts ?? 0} guesses</p>
  </section>;
}

export function WordScramble({ state, send }: GameProps) {
  const [answers, setAnswers] = useState<Record<number, string>>({});
  const items = state.activity?.config.scrambleItems?.length
    ? state.activity.config.scrambleItems
    : [{ scrambled: state.activity?.config.scrambled ?? "", clue: state.activity?.config.clue }];
  const solved = state.player?.score?.solvedItems ?? [];
  return <section className="game-card">
    <p className="eyebrow">UNSCRAMBLE {items.length === 1 ? "THIS" : "THESE"}</p>
    <div className="scramble-play-list">{items.map((item, index) => <article className={solved.includes(index) ? "solved" : ""} key={index}>
      <div className="scramble-item-heading"><span>{index + 1}</span><div className="scramble-letters">{item.scrambled}</div></div>
      {item.clue && <p className="clue">💡 {item.clue}</p>}
      {solved.includes(index) ? <div className="success-panel">✓ Solved</div> : <form onSubmit={(event) => { event.preventDefault(); const value = answers[index]?.trim(); if (value) { send({ type: "scramble_answer", index, value }); setAnswers({ ...answers, [index]: "" }); } }}>
        <input value={answers[index] ?? ""} onChange={(event) => setAnswers({ ...answers, [index]: event.target.value })} placeholder="Type the answer" autoCapitalize="off" autoComplete="off" autoFocus={index === 0} />
        <button className="button button--primary" type="submit">SUBMIT</button>
      </form>}
    </article>)}</div>
    <p className="attempts">{solved.length}/{items.length} solved · Unlimited attempts · {state.player?.score?.attempts ?? 0} tried</p>
  </section>;
}

type Cell = { row: number; col: number };

function cellsBetween(start?: Cell, end?: Cell): string[] {
  if (!start || !end) return [];
  const rowDistance = end.row - start.row;
  const columnDistance = end.col - start.col;
  if (!(rowDistance === 0 || columnDistance === 0 || Math.abs(rowDistance) === Math.abs(columnDistance))) return [];
  const rowStep = Math.sign(rowDistance), columnStep = Math.sign(columnDistance);
  const length = Math.max(Math.abs(rowDistance), Math.abs(columnDistance));
  return Array.from({ length: length + 1 }, (_, index) => `${start.row + rowStep * index}-${start.col + columnStep * index}`);
}

export function WordSearch({ state, send }: GameProps) {
  const grid = state.activity?.config.grid ?? [];
  const words = state.activity?.config.words ?? [];
  const found = useMemo(() => state.player?.score?.foundWords ?? [], [state.player?.score?.foundWords]);
  const [start, setStart] = useState<Cell>();
  const [hover, setHover] = useState<Cell>();
  const [tapAnchor, setTapAnchor] = useState<Cell>();
  const [pointerActive, setPointerActive] = useState(false);
  const selectedKeys = useMemo(() => cellsBetween(start, hover), [start, hover]);
  const selected = useMemo(() => new Set(selectedKeys), [selectedKeys]);
  const foundCells = useMemo(() => {
    const keys = new Set<string>();
    for (const placement of state.activity?.config.placements ?? []) {
      if (!found.includes(placement.word)) continue;
      cellsBetween({ row: placement.startRow, col: placement.startCol }, { row: placement.endRow, col: placement.endCol }).forEach((key) => keys.add(key));
    }
    return keys;
  }, [found, state.activity?.config.placements]);
  const setCurrentCell = (cell: Cell) => setHover(cell);
  const beginSelection = (event: ReactPointerEvent<HTMLButtonElement>, cell: Cell) => {
    event.preventDefault(); event.currentTarget.setPointerCapture(event.pointerId);
    const gridElement = event.currentTarget.parentElement;
    if (gridElement) { gridElement.dataset.startRow = String(cell.row); gridElement.dataset.startCol = String(cell.col); }
    setPointerActive(true); setStart(cell); setHover(cell);
  };
  const submitSelection = (selectionStart = start, end = hover) => {
    setPointerActive(false);
    const keys = cellsBetween(selectionStart, end);
    setTapAnchor(undefined);
    if (keys.length === 0) { setStart(undefined); setHover(undefined); return; }
    const chars = keys.map((key) => { const [row, col] = key.split("-").map(Number); return grid[row]?.[col] ?? ""; }).join("");
    const reversed = [...chars].reverse().join("");
    const match = words.find((word) => word === chars || word === reversed);
    if (match) send({ type: "word_found", value: match });
    setStart(undefined); setHover(undefined);
  };
  const handleTap = (cell: Cell, gridElement: HTMLElement | null) => {
    if (gridElement?.dataset.ignoreClick === "1") { delete gridElement.dataset.ignoreClick; return; }
    if (!tapAnchor) { setTapAnchor(cell); setStart(cell); setHover(cell); return; }
    submitSelection(tapAnchor, cell);
  };
  return <section className="word-search-wrap">
    <div className="word-list">{words.map((word) => <span className={found.includes(word) ? "found" : ""} key={word}>{found.includes(word) ? "✓ " : ""}{word}</span>)}</div>
    <div className="word-grid" style={{ gridTemplateColumns: `repeat(${grid.length}, 1fr)` }}
      onPointerMove={(event) => { if (!pointerActive) return; const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-word-cell]"); if (!target) return; setCurrentCell({ row: Number(target.dataset.row), col: Number(target.dataset.col) }); }}
      onPointerUp={(event) => {
        const target = document.elementFromPoint(event.clientX, event.clientY)?.closest<HTMLElement>("[data-word-cell]");
        const savedStart = event.currentTarget.dataset.startRow !== undefined && event.currentTarget.dataset.startCol !== undefined
          ? { row: Number(event.currentTarget.dataset.startRow), col: Number(event.currentTarget.dataset.startCol) }
          : start;
        const end = target ? { row: Number(target.dataset.row), col: Number(target.dataset.col) } : hover;
        delete event.currentTarget.dataset.startRow; delete event.currentTarget.dataset.startCol;
        if (cellsBetween(savedStart, end).length > 1) {
          const gridElement = event.currentTarget;
          gridElement.dataset.ignoreClick = "1";
          window.setTimeout(() => { delete gridElement.dataset.ignoreClick; }, 0);
          submitSelection(savedStart, end);
        } else {
          setPointerActive(false);
        }
      }} onPointerCancel={(event) => { delete event.currentTarget.dataset.startRow; delete event.currentTarget.dataset.startCol; submitSelection(); }}>
      {grid.flatMap((row, rowIndex) => [...row].map((char, colIndex) => {
        const key = `${rowIndex}-${colIndex}`;
        return <button key={key} data-word-cell data-row={rowIndex} data-col={colIndex} className={`${foundCells.has(key) ? "found-cell" : ""} ${selected.has(key) ? "selected" : ""}`}
          onPointerDown={(event) => beginSelection(event, { row: rowIndex, col: colIndex })}
          onClick={(event) => handleTap({ row: rowIndex, col: colIndex }, event.currentTarget.parentElement)}
          aria-label={`${char}, row ${rowIndex + 1}, column ${colIndex + 1}`}>{char}</button>;
      }))}
    </div>
    <p className="attempts">Swipe across a word, or tap its first and last letter · {found.length}/{words.length} found</p>
  </section>;
}

export function Puzzle({ state, send }: GameProps) {
  const puzzleSeed = `${state.activity?.id ?? "puzzle"}:${state.activity?.config.shuffleVersion ?? 0}:${state.player?.participant.id ?? "participant"}`;
  const [pieces, setPieces] = useState(() => createPuzzlePieces(puzzleSeed));
  const [selected, setSelected] = useState<number>();
  const imageUrl = state.activity?.config.imageUrl ?? "";
  const [imageAttempt, setImageAttempt] = useState(0);
  const [imageStatus, setImageStatus] = useState<"loading" | "ready" | "error">("loading");
  const resolvedImageUrl = imageAttempt > 0 ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}retry=${imageAttempt}` : imageUrl;
  useEffect(() => {
    let active = true;
    setImageStatus("loading");
    if (!resolvedImageUrl) { setImageStatus("error"); return () => { active = false; }; }
    const image = new Image();
    const timeout = window.setTimeout(() => { if (active) setImageStatus("error"); }, 12_000);
    image.onload = () => { window.clearTimeout(timeout); if (active) setImageStatus("ready"); };
    image.onerror = () => { window.clearTimeout(timeout); if (active) setImageStatus("error"); };
    image.src = resolvedImageUrl;
    return () => { active = false; window.clearTimeout(timeout); image.onload = null; image.onerror = null; };
  }, [resolvedImageUrl]);
  const locked = pieces.map((piece, index) => piece === index);
  const progress = locked.filter(Boolean).length;
  const swapPieces = (first: number, second: number) => {
    if (first === second || locked[first] || locked[second]) { setSelected(undefined); return; }
    const next = [...pieces]; [next[first], next[second]] = [next[second], next[first]]; setPieces(next); setSelected(undefined);
    const nextProgress = next.filter((piece, index) => piece === index).length;
    if (nextProgress > progress) send({ type: "puzzle_progress", value: nextProgress, completed: nextProgress === 9 });
  };
  return <section className="puzzle-wrap">
    {imageStatus === "loading" && <div className="puzzle-image-state"><span className="spinner" /><h2>Loading puzzle image…</h2><p>Please keep this screen open.</p></div>}
    {imageStatus === "error" && <div className="puzzle-image-state puzzle-image-state--error"><span>🖼️</span><h2>Puzzle image couldn’t load</h2><p>Check your connection, then try again. If it still fails, open this link in Chrome or Safari.</p><button className="button button--primary" onClick={() => setImageAttempt(Date.now())}>RETRY IMAGE</button></div>}
    {imageStatus === "ready" && <div className="puzzle-grid">
      {pieces.map((piece, index) => <button key={index} className={`${locked[index] ? "locked" : ""} ${selected === index ? "selected" : ""}`}
        onClick={() => selected === undefined ? setSelected(index) : swapPieces(selected, index)}
        draggable={!locked[index]}
        onDragStart={(event) => { setSelected(index); event.dataTransfer.setData("text/plain", String(index)); }}
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => { event.preventDefault(); swapPieces(Number(event.dataTransfer.getData("text/plain")), index); }}
        style={{ backgroundImage: `url(${resolvedImageUrl})`, backgroundPosition: `${(piece % 3) * 50}% ${Math.floor(piece / 3) * 50}%` }}
        aria-label={`Puzzle piece ${piece + 1} in position ${index + 1}`} />)}
    </div>}
    <p className="puzzle-help">Every piece starts in the wrong position. Tap two pieces to swap, or drag one into place.</p>
    <div className="progress-bar"><i style={{ width: `${(progress / 9) * 100}%` }} /></div><strong>{progress}/9 correct</strong>
  </section>;
}

export function ResponseActivity({ state, send }: GameProps) {
  const questions = state.activity?.config.responseQuestions ?? [];
  const saved = useMemo(() => state.player?.score?.responses ?? {}, [state.player?.score?.responses]);
  const [drafts, setDrafts] = useState<Record<string, string>>(() => ({ ...saved }));
  useEffect(() => {
    setDrafts((current) => {
      const next = { ...current };
      for (const [index, value] of Object.entries(saved)) if (next[index] === undefined) next[index] = value;
      return next;
    });
  }, [saved]);
  return <section className="response-participant">
    <div className="response-participant-intro"><p className="eyebrow">SHARE YOUR THOUGHTS</p><h2>{questions.length === 1 ? "Your response" : `${questions.length} questions`}</h2><p>{state.activity?.config.responseShowNames === false ? "Your response appears anonymously on the big screen." : "Your response appears live on the big screen with your name."}</p></div>
    <div className="response-question-list">{questions.map((question, index) => {
      const key = String(index); const value = drafts[key] ?? ""; const submitted = saved[key] !== undefined;
      return <article key={key}><header><span>{index + 1}</span><strong>{question}</strong></header><form className="response-form" onSubmit={(event) => { event.preventDefault(); const clean = value.trim(); if (clean) send({ type: "response_submit", questionIndex: index, value: clean }); }}>
        <textarea rows={3} maxLength={180} value={value} onChange={(event) => setDrafts({ ...drafts, [key]: event.target.value })} placeholder="Type your response…" required />
        <div><small>{value.length}/180</small><button className={submitted ? "button button--secondary" : "button button--primary"} type="submit">{submitted ? "UPDATE RESPONSE" : "SUBMIT RESPONSE"}</button></div>
      </form>{submitted && <p className="response-saved">✓ Response submitted</p>}</article>;
    })}</div>
  </section>;
}

export function ActiveGame(props: GameProps) {
  const type = props.state.activity?.type;
  if (type === "guess-number") return <GuessNumber {...props} />;
  if (type === "word-scramble") return <WordScramble {...props} />;
  if (type === "word-search") return <WordSearch {...props} />;
  if (type === "puzzle") return <Puzzle key={props.state.activity?.id} {...props} />;
  if (type === "response") return <ResponseActivity {...props} />;
  return null;
}
